import React, { useState, useEffect } from 'react';
import { Box, Spinner, StackView, Typography } from '@telus-uds/components-web';
import { useLocalizedStringsContext } from '../../../context/LocalizedStringsContext';

import { useDevices, useNotifications, useForm } from './hooks';
import DeviceForm from './DeviceForm';
import DeviceTable from './DeviceTable';
import NotificationBanner from './NotificationBanner';
import DeleteModal from './DeleteModal';
import { DEVICES_PER_PAGE } from './constants';

const DeviceMatrix = () => {
  // Locale functionality
  const localizedStrings = useLocalizedStringsContext();
  const { DEVICE_MATRIX = {} } = localizedStrings || {};
  
  // Custom hooks
  const {
    devices,
    loading,
    currentPage,
    totalPages,
    totalDevices,
    fetchDevices,
    addDevice,
    updateDevice,
    deleteDevice,
    handlePageChange,
    handlePreviousPage,
    handleNextPage
  } = useDevices(DEVICES_PER_PAGE);
  
  const { notification, showNotification } = useNotifications();
  const { editingId, formValues, handleEdit, resetForm } = useForm();
  
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Fetch devices on component mount
  useEffect(() => {
    fetchDevices();
  }, []);

  // Don't render until translations are loaded
  if (!localizedStrings || !DEVICE_MATRIX || Object.keys(DEVICE_MATRIX).length === 0) {
    return <Spinner fullScreen label="Loading..." show />;
  }

  const handleFormSubmit = async (values, { resetForm: formikResetForm }) => {
    try {
      if (editingId) {
        await updateDevice(editingId, values);
        showNotification((DEVICE_MATRIX.MESSAGES && DEVICE_MATRIX.MESSAGES.DEVICE_UPDATED) || 'Device updated successfully', 'success');
      } else {
        await addDevice(values);
        showNotification((DEVICE_MATRIX.MESSAGES && DEVICE_MATRIX.MESSAGES.DEVICE_ADDED) || 'Device added successfully', 'success');
      }
      
      formikResetForm();
      resetForm(); // This clears editingId and resets form to "Add New Device" mode
    } catch (error) {
      showNotification((DEVICE_MATRIX.MESSAGES && DEVICE_MATRIX.MESSAGES.ERROR_UPDATING) || 'Error saving device', 'error');
    }
  };

  const handleDelete = (device) => {
    setDeleteConfirm({
      id: device.id,
      name: device.name
    });
  };

  const confirmDelete = async () => {
    if (deleteConfirm) {
      try {
        await deleteDevice(deleteConfirm.id);
        showNotification((DEVICE_MATRIX.MESSAGES && DEVICE_MATRIX.MESSAGES.DEVICE_DELETED) || 'Device deleted successfully', 'success');
        setDeleteConfirm(null);
      } catch (error) {
        showNotification((DEVICE_MATRIX.MESSAGES && DEVICE_MATRIX.MESSAGES.ERROR_DELETING) || 'Error deleting device', 'error');
        setDeleteConfirm(null);
      }
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  return (
    <StackView space={4}>
      <Typography heading="h2" variant={{ size: 'h2' }} block>{DEVICE_MATRIX.TITLE || 'Device Matrix'}</Typography>
      
      {/* Form Section */}
      <DeviceForm
        editingId={editingId}
        formValues={formValues}
        onSubmit={handleFormSubmit}
        onReset={resetForm}
        onCancelEdit={handleCancelEdit}
        DEVICE_MATRIX={DEVICE_MATRIX}
      />

      {/* Notification Banner */}
      <NotificationBanner 
        notification={notification} 
        onClose={() => showNotification(null)} 
      />

      {/* Table Section */}
      <DeviceTable
        devices={devices}
        loading={loading}
        totalDevices={totalDevices}
        currentPage={currentPage}
        totalPages={totalPages}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onPageChange={handlePageChange}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        DEVICE_MATRIX={DEVICE_MATRIX}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        deleteConfirm={deleteConfirm}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        DEVICE_MATRIX={DEVICE_MATRIX}
      />
    </StackView>
  );
};

export default DeviceMatrix;
